import React, { useEffect, useRef } from 'react'

import classNames from 'classnames'

import { DiningOptionBehavior } from 'src/apollo/onlineOrdering'
import { useRestaurant } from 'src/shared/components/common/restaurant_context/RestaurantContext'
import { t } from 'src/translations'

import { DEFAULT_COLORS } from 'public/components/default_template/meta/StyleMeta'
import { useThemeColorScheme } from 'public/components/default_template/meta/useTheme'
import { TagIcon } from 'public/components/default_template/offers/OffersIcons'
import {
  isFreeDeliveryThresholdReached,
  useAmountNeededMessage,
  useFreeDeliveryOffer
} from 'public/components/default_template/offers/useFreeDeliveryOffer'
import type { FreeDeliveryOfferData } from 'public/components/default_template/offers/useFreeDeliveryOffer'
import { getCheckoutEligibleItems } from 'public/components/default_template/online_ordering/cart/cartUtils'
import { useCart } from 'public/components/online_ordering/CartContext'
import { useCustomer } from 'public/components/online_ordering/CustomerContextCommon'
import { useFulfillment } from 'public/components/online_ordering/FulfillmentContext'
import { useA11y } from 'public/components/online_ordering/useA11y'

const ProgressBanner = ({
  offer,
  signedIn
}: {
  offer: FreeDeliveryOfferData
  signedIn: boolean
}) => {
  const { announce } = useA11y()
  const amountNeededMessage = useAmountNeededMessage(offer)
  const { restaurant } = useRestaurant()
  const getColorFromTheme = useThemeColorScheme()

  const thresholdReached = isFreeDeliveryThresholdReached(offer)

  // Same source as the restaurant-funded offer row's tag, so the two icons match
  // in the cart.
  const tagColor = thresholdReached
    ? '#408140'
    : getColorFromTheme(
        (theme) => theme.colorScheme.icon.action,
        restaurant?.meta?.primaryColor || DEFAULT_COLORS.primary
      )

  let primaryLine
  if (!thresholdReached) {
    primaryLine = amountNeededMessage
  } else if (signedIn) {
    primaryLine = t('free-delivery-progress.free-delivery-applied')
  } else {
    primaryLine = t('free-delivery-progress.free-delivery-unlocked')
  }

  // Editing the cart rewrites this line in place, which a screen reader would
  // otherwise pass over silently. Skip the first render so opening the cart does
  // not talk over the reader.
  const announced = useRef<string>()
  useEffect(() => {
    if (announced.current && announced.current !== primaryLine) {
      announce(primaryLine)
    }
    announced.current = primaryLine
  }, [announce, primaryLine])

  return (
    <div className='freeDeliveryProgress' data-testid='free-delivery-progress'>
      {!thresholdReached && (
        <div
          className='progressTrack'
          data-testid='free-delivery-progress-track'
        >
          <div
            className='progressFill'
            data-testid='free-delivery-progress-fill'
            style={{ width: `${Math.max(offer.percentCompleteInt, 0)}%` }}
          />
        </div>
      )}
      <div
        className={classNames('progressStatus', {
          applied: thresholdReached,
          hasSecondaryLine: !signedIn
        })}
        data-testid='free-delivery-progress-status'
      >
        <TagIcon color={tagColor} altText={t('cart-offer.offer')} />
        <div>
          <div className='primaryLine' data-testid='free-delivery-primary-line'>
            {primaryLine}
          </div>
          {!signedIn && (
            <div
              className='secondaryLine'
              data-testid='free-delivery-secondary-line'
            >
              {t('free-delivery-progress.sign-in-to-redeem')}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const FreeDeliveryProgress = () => {
  const offer = useFreeDeliveryOffer()
  const { cart } = useCart()
  const { selectedDiningOptionBehavior } = useFulfillment()
  const { isLoggedIn, loadingCustomer } = useCustomer()
  const signedIn = isLoggedIn || loadingCustomer

  const hasItems = !!getCheckoutEligibleItems(cart)?.length
  const isDelivery =
    selectedDiningOptionBehavior === DiningOptionBehavior.Delivery

  if (!offer || !isDelivery || !hasItems) {
    return null
  }

  return <ProgressBanner offer={offer} signedIn={signedIn} />
}

export default FreeDeliveryProgress
